import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'universal-cookie';
import Swal from 'sweetalert2';
import { ArrowRightCircle, Eye, EyeSlash, Person } from 'react-bootstrap-icons';

import ForgetPassModel from './ForgetPassModel';
import SignupModal from './SignupModal';
import GuestLoginModal from './GuestLoginModal';
import Button from '../common/Button';
import { notifyAuthChange } from '../../hooks/useAuth';
import { apiUrl } from '../../config/api';
import logo from '../../images/amazing gems and jewellery png with name.png';
import './LoginModal.scss';

const LoginModal = ({ checkout = '', show, onClose }) => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const [signupShow, setSignupShow] = useState(false);
  const [guestShow, setGuestShow] = useState(false);
  const [forgotShow, setForgotShow] = useState(false);

  // Clear the form whenever the modal is dismissed, so reopening it never
  // shows a stale password or an old error.
  useEffect(() => {
    if (!show) {
      setPassword('');
      setFormError(null);
      setShowPassword(false);
    }
  }, [show]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    setLoading(true);
    setFormError(null);

    try {
      const response = await fetch(apiUrl('/api/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          common_id: localStorage.getItem('common_id') || '',
          email,
          password,
        }),
      });
      const data = await response.json();

      if (data.token && data.user_id) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user_id', data.user_id);
        localStorage.setItem('user_name', data.user_name || '');

        const { exp } = jwtDecode(data.token);
        new Cookies().set('user_id', data.user_id, {
          expires: new Date(exp * 1000),
          path: '/',
        });

        notifyAuthChange();
        onClose();
        setEmail('');
        setPassword('');

        if (checkout) navigate('/check/checkoutpage');

        Swal.fire({
          icon: 'success',
          confirmButtonColor: '#b63f23',
          title: 'Welcome back!',
        });
      } else {
        // Show the failure inside the form rather than closing the modal,
        // so the shopper can correct their details in place.
        setFormError(data.message || 'Invalid email or password.');
      }
    } catch {
      setFormError('We could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onClose} centered>
        <Modal.Header closeButton>Sign in</Modal.Header>
        <Modal.Body>
          <div className="ij-auth">
            <img className="ij-auth__logo" src={logo} alt="Indian Jewelryish" />

            <form className="ij-auth__form" onSubmit={handleSubmit} noValidate>
              <div className="ij-field">
                <label className="ij-label" htmlFor="login-email">
                  Email address
                </label>
                <input
                  id="login-email"
                  type="email"
                  className="ij-input"
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={email}
                  required
                  disabled={loading}
                  aria-invalid={formError ? 'true' : undefined}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>

              <div className="ij-field">
                <label className="ij-label" htmlFor="login-password">
                  Password
                </label>
                <div className="ij-auth__password">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    className="ij-input"
                    placeholder="Your password"
                    autoComplete="current-password"
                    value={password}
                    required
                    disabled={loading}
                    aria-invalid={formError ? 'true' : undefined}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <button
                    type="button"
                    className="ij-auth__password-toggle"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((visible) => !visible)}
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {formError && (
                <p className="ij-alert ij-alert--error" role="alert">
                  {formError}
                </p>
              )}

              <Button
                type="submit"
                variant="primary"
                block
                loading={loading}
                loadingText="Signing in…"
              >
                Sign in
              </Button>

              <button
                type="button"
                className="ij-btn ij-btn--link"
                disabled={loading}
                onClick={() => {
                  onClose();
                  setForgotShow(true);
                }}
              >
                Forgot your password?
              </button>
            </form>

            <div className="ij-auth__divider">
              <span>or</span>
            </div>

            <div className="ij-auth__alternatives">
              <Button
                variant="outline"
                block
                disabled={loading}
                onClick={() => {
                  onClose();
                  setGuestShow(true);
                }}
              >
                <Person size={18} aria-hidden="true" />
                Continue as guest
              </Button>
              <Button
                variant="ghost"
                block
                disabled={loading}
                onClick={() => {
                  onClose();
                  setSignupShow(true);
                }}
              >
                <ArrowRightCircle size={18} aria-hidden="true" />
                Create an account
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <ForgetPassModel show={forgotShow} onClose={() => setForgotShow(false)} />
      <SignupModal show={signupShow} onClose={() => setSignupShow(false)} />
      <GuestLoginModal show={guestShow} onClose={() => setGuestShow(false)} />
    </>
  );
};

export default LoginModal;
